import MembershipDetailsHeader from "@/components/molecules/headers/details-page/MembershipDetailsHeader";
import DetailPageInfo from "@/components/organisms/purchases/DetailPageInfo";
import DetailPageLayout from "@/components/organisms/purchases/DetailPageLayout";
import DetailPageModal from "@/components/organisms/purchases/modals/DetailPageModal";
import withQueryClient, {
  GetServerSidePropsContextWithQueryClient,
} from "@/gssp-subroutines/withQueryClient";
import lang from "@/helpers/lang";
import useMediaQuery from "@/hooks/useMediaQuery";
import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import MembershipTypesList from "@shared/modules/memberships/components/MembershipTypesList/MembershipTypesList";
import Footer from "@ui-application/navigation/footer/Footer/Footer.web";

import { breakpoints } from "@/styles/constants";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import PublicBasedMembershipGroupRepository from "@shared/modules/memberships/data/repositories/PublicBasedMembershipGroupRepository";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";

import { waitForElementToLoad } from "@/helpers/loader-utils";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { Spinner } from "@app/core/src/shared/components/ui-kit/feedback/Spinner/Spinner.web";
import ContactDetailsModalSelection from "@app/core/src/shared/modules/seating/components/contactDetailsModal/ContactDetailsModalSelection";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import GridItem from "@ui-kit/layout/GridItem/GridItem.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import styled from "@emotion/styled";
import PublicBasedVenuesRepository from "@shared/modules/venue/repositories/PublicBasedVenuesRepository";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import { InferGetServerSidePropsType } from "next";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import Script from "next/script";
import React, { useEffect, useMemo, useState } from "react";
import { dehydrate, useQueryClient } from "react-query";
import { AnalyticsService } from "@app/core/src/shared/services/analytics";

const { sm, lg } = breakpoints;

const SpinnerContainer = styled(Flex)`
  flex-direction: column;
  background-color: var(--clr-paint-gray-200);
  flex-grow: 1;
`;

const membershipRepo = new PublicBasedMembershipGroupRepository();
const venueRepo = new PublicBasedVenuesRepository();

const queryKeyManager = new QueryKeyManager(venueRepo);

const MembershipDetailPage = ({
  membershipGroupData: initialMembershipGroupData,
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { isReactNativeApp } = useNativeAppCheck();

  const {
    isOpen: isContactDetailsOpen,
    onOpen: onOpenContactDetails,
    onClose: onCloseContactDetails,
  } = useDisclosure();

  const {
    isOpen: isMembershipModalOpen,
    onOpen: onMembershipModalOpen,
    onClose: onMembershipModalClose,
  } = useDisclosure();

  const membershipGroupParams = {
    purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
    ].toString(),
  };

  const { useRetrieve } = usePublicMembershipGroupsService();
  const { useRetrieve: useVenueRetrieve } = usePublicVenueService();
  const {
    data: membership_group,
    isLoading,
    isError,
  } = useRetrieve(String(router?.query?.membershipSlug), {
    config: { params: membershipGroupParams },
    options: {
      initialData: initialMembershipGroupData,
      enabled: Boolean(router?.query?.membershipSlug && membershipGroupParams),
    },
  });

  useEffect(() => {
    AnalyticsService.membershipLandingPageViewed(membership_group);
  }, [membership_group]);

  const queryClient = useQueryClient();

  const { data: venueObject } = useVenueRetrieve(
    Number(membership_group?.venue?.id),
    {
      options: {
        initialData: queryClient.getQueryData(
          queryKeyManager.generateKey([
            "retrieve",
            Number(initialMembershipGroupData?.venue?.id),
          ])
        ),
        enabled: Boolean(initialMembershipGroupData?.venue?.id),
      },
    }
  );

  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();

  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm - 1}px)`);
  const [isTablet] = useMediaQuery(
    `(min-width: 0px) and (max-width: ${lg - 1}px)`
  );

  const venueInfo = {
    venueId: membership_group?.venue?.id,
    venueSlug: membership_group?.venue?.slug,
    venueDescription: venueObject?.description,
    venueName: membership_group?.venue?.name,
    currency: membership_group?.venue?.currency,
    venuePrivate: venueObject?.is_test,
  };

  const membershipTabOptions = useMemo(
    () => [
      {
        id: 0,
        title: t("Additional info", { ns: "common" }),
        showTab: true,
        hash: "additional-info",
      },
    ],
    [t]
  );

  useEffect(() => {
    if (isError) {
      router.push({ pathname: "/404/" }, undefined, { shallow: true });
    } else if (!router?.query?.membershipSlug) {
      router.push({ pathname: "/" }, undefined, { shallow: true });
    }
  });

  // initialize the selected tab button to nothing
  const [selectedTab, setSelectedTab] = useState(-1);

  // update the selected tab from pressing the browser's back/forward buttons
  useEffect(() => {
    const validHash = membershipTabOptions?.find(
      (tab) => tab.hash === router?.asPath?.split("#")[1]
    );
    const hashNeedsUpdating =
      router?.asPath?.split("#")[1] !==
      membershipTabOptions.find((tab) => tab.id === selectedTab)?.hash;

    if (validHash && hashNeedsUpdating) {
      setSelectedTab(
        Number(
          membershipTabOptions.find(
            (tab) => tab.hash === router?.asPath?.split("#")[1]
          )?.id
        )
      );
    }

    // autoscroll to the page elements
    if (document.readyState === "complete") {
      const elementID = router?.asPath?.split("#")[1];
      waitForElementToLoad(`#${elementID}`).then(() =>
        document
          .getElementById(`${elementID}`)
          ?.scrollIntoView({ behavior: "smooth" })
      );
    }
  }, [selectedTab, router?.asPath, membershipTabOptions]);

  const getPageDisplay = () => {
    return (
      <DetailPageInfo
        infoHeader={t("Description")}
        infoText={membership_group?.description || ""}
        refundPolicyText={String(venueObject?.default_refund_policy)}
        isLoading={isLoading || !venueObject}
        openContactDetailsModal={onOpenContactDetails}
        venueInfo={venueInfo}
        isAttractionPage={false}
      />
    );
  };

  const showPageContent = !isError && !isLoading && !!membership_group;
  const showSpinner = isLoading || isError || !membership_group;

  return (
    <Flex h="full" w="full" direction="column" flexGrow={1} bg="gray.200">
      {/* Contact details modal, opens from the Contact Information section */}
      <ContactDetailsModalSelection
        venueId={venueInfo.venueId}
        isOpen={isContactDetailsOpen}
        onClose={onCloseContactDetails}
        user={user}
        isLoggedIn={isLoggedIn}
      />
      {/* List of membership levels for purchase */}
      <DetailPageModal
        isOpen={isMembershipModalOpen}
        onClose={onMembershipModalClose}
        title={t("Memberships")}
        venue={venueObject}
        holdLink={router?.query?.link as string}
        itemListElement={
          venueObject && membership_group ? (
            <MembershipTypesList
              membershipGroup={membership_group}
              membershipLevels={membership_group?.membership_levels ?? []}
              venue={venueObject}
              currency={venueObject.currency}
              hasAllInPricing={venueObject?.all_in_pricing_is_enabled}
              isLoading={isLoading}
            />
          ) : (
            <></>
          )
        }
      />

      {/* Rich schema implementation for membership group object */}
      {membership_group && membership_group.membershipGroupSchema && (
        <Script id="membership-group-schema" type="application/ld+json">
          {membership_group.membershipGroupSchema}
        </Script>
      )}

      {showPageContent && (
        <MembershipDetailsHeader
          membershipTabOptions={membershipTabOptions}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          isSmallerThanSm={isSmallerThanSm}
          isTablet={isTablet}
          isReactNativeApp={isReactNativeApp}
          venue={venueObject}
          openModal={onMembershipModalOpen}
        />
      )}
      {/* Spinner that displays while loading membership data */}
      {showSpinner && (
        <SpinnerContainer alignItems="center" justifyContent="center">
          <Spinner
            emptyColor="var(--clr-paint-gray-400)"
            color="var(--clr-paint-secondary-600)"
            speed="0.65s"
            size="3xl"
            thickness="10px"
          />
        </SpinnerContainer>
      )}
      <Box
        hidden={showSpinner || !showPageContent}
        w="full"
        mx="auto"
        flexGrow={1}
        maxWidth="container.content"
        px={{ base: "none", sm: "3xl" }}
        bg="gray.200"
      >
        {/* Page content for membership information */}
        <Flex py={isTablet ? "4" : "6"} direction="column">
          <Grid h="full" width="full" gap="xl" templateColumns="repeat(5, 1fr)">
            <GridItem colSpan={isTablet ? 5 : 3} height="fit-content">
              <VStack
                id="additional-info"
                scrollMarginTop={useDetailPageScrollMargin()}
              >
                {getPageDisplay()}
              </VStack>
            </GridItem>
          </Grid>
        </Flex>
      </Box>
      <Box display={{ base: "none", sm: "block" }}>
        <Footer isReactNativeApp={isReactNativeApp} />
      </Box>
    </Flex>
  );
};

export default MembershipDetailPage;

MembershipDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  return (
    <DetailPageLayout
      itemData={page.props.membershipGroupData}
      analyticsLabel={enums.INTERACTION_LABEL_MAP.MEMBERSHIP_PAGE.id}
      trackingTokenLocation={
        enums.TRACKING_TOKEN_LOCATIONS.LOCATION_PUBLIC_MEMBERSHIP.id
      }
    >
      {page}
    </DetailPageLayout>
  );
};

type GSSPType = {
  membershipGroupData: PublicMembershipGroupSerializer;
};

export const getServerSideProps = withQueryClient(
  async ({
    locale,
    params,
    queryClient,
  }: GetServerSidePropsContextWithQueryClient) => {
    let membershipGroupData: PublicMembershipGroupSerializer | null = null;

    /**
     * Prefetch membership data
     */
    try {
      const { membershipSlug } = params;
      const membershipParams = {
        purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
        payment_type: [
          PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
          PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
          PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
        ].toString(),
      };

      if (membershipSlug) {
        membershipGroupData = await membershipRepo.retrieve(
          typeof membershipSlug === "string"
            ? membershipSlug
            : membershipSlug[0],
          { params: membershipParams }
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    /**
     * Prefetch venue data for memberships
     */
    try {
      if (membershipGroupData && membershipGroupData.venue) {
        await queryClient.prefetchQuery(
          queryKeyManager.generateKey([
            "retrieve",
            membershipGroupData.venue.id,
          ]),
          async () => venueRepo.retrieve(membershipGroupData.venue.id)
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    // If membershipGroupData is falsy, redirect to 404 page.
    // This will avoid the [membershipSlug] page completely.
    if (!membershipGroupData) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        ...(await lang(locale, ["purchases", "account", "constants"])),
        membershipGroupData,
      },
    } as {
      props: GSSPType;
    };
  }
);
